import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { base_url } from "../config/config";
import { saveToken, removeToken } from "../actions/localstore";
import { logAuthEvent } from "../actions/authSessionTracker";

const saveSessionUserInfo = (user = {}) => {
  const email = user?.email || "";
  const userName =
    `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
    user?.name ||
    "";
  const userId = user?._id || user?.id || "";

  sessionStorage.setItem("userEmail", email);
  sessionStorage.setItem("userName", userName);
  sessionStorage.setItem("userId", userId);
};

const clearSessionUserInfo = () => {
  sessionStorage.removeItem("userEmail");
  sessionStorage.removeItem("userName");
  sessionStorage.removeItem("userId");
};

export const loginUser = createAsyncThunk(
  "user/loginUser",
  async (userCreds, { rejectWithValue }) => {
    try {
      // For development purposes, allow a direct login with test credentials
      if (
        process.env.NODE_ENV === "development" &&
        userCreds.email === "admin@example.com" &&
        userCreds.password === "admin123"
      ) {
        console.log("Development mode: Using mock login");

        // Mock token and user data
        const mockToken =
          "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6ImRldmVsb3BtZW50X3Rlc3RfdXNlciIsImVtYWlsIjoidGVzdEBleGFtcGxlLmNvbSIsInVzZXJ0eXBlIjoiYWRtaW4iLCJuYW1lIjoiVGVzdCBVc2VyIiwiaWF0IjoxNjE5NzE3MjUxLCJleHAiOjE5MzUwNzcyNTF9.TbzdlG0UnPTB4fBXH1RQz4SbAjqaiPZn8XTyYxwTr_A";
        const mockUser = {
          _id: "development_test_user",
          name: "Admin User",
          email: "admin@example.com",
          token: mockToken,
        };

        // Save the token and return mock user data
        saveToken(mockToken);
        saveSessionUserInfo(mockUser);
        await logAuthEvent({ action: "login", user: mockUser, source: "web" });
        return mockUser;
      }

      // Regular API login for production
      const response = await fetch(`${base_url}/v1/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: userCreds.email,
          password: userCreds.password,
        }),
      });

      const userData = await response.json();
      console.log("Login response:", userData);

      if (!response.ok) {
        return rejectWithValue(userData.message);
      }
      saveToken(userData.message.token);
      saveSessionUserInfo(userData.message);
      await logAuthEvent({
        action: "login",
        user: userData.message,
        source: "web",
      });
      return userData.message;
    } catch (error) {
      console.error("Login error:", error);
      return rejectWithValue("Network error, please try again.");
    }
  },
);

export const logoutUser = createAsyncThunk(
  "user/logoutUser",
  async (_, { rejectWithValue, getState }) => {
    try {
      const userFromState = getState()?.user?.user || {};
      const user = {
        ...userFromState,
        _id: userFromState?._id || sessionStorage.getItem("userId") || "",
        email:
          userFromState?.email || sessionStorage.getItem("userEmail") || "",
        name: userFromState?.name || sessionStorage.getItem("userName") || "",
      };

      await logAuthEvent({ action: "logout", user, source: "manual_logout" });
      removeToken();
      clearSessionUserInfo();
      return true;
    } catch (error) {
      return rejectWithValue("Failed to log out, please try again.");
    }
  },
);

const UserSlice = createSlice({
  name: "user",
  initialState: {
    loading: false,
    user: null,
    error: null,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.user = null;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        if (action.payload === "User Does not exist") {
          state.error = "Benutzer nicht gefunden";
        } else if (action.payload === "Incorrect Password") {
          state.error = "Falsches Passwort";
        } else {
          state.error = action.payload;
        }
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.error = null;
      });
  },
});

export default UserSlice.reducer;
